/**
 * Tiny abstract base for studio panels. Each panel:
 *   1. Builds its DOM lazily on first `mount`.
 *   2. Toggles visibility via `show` / `hide` (class-based, not
 *      `display: none`, so the PanelManager can crossfade between
 *      panels with CSS transitions).
 *   3. Tears down on `destroy` (unsubscribing store listeners, etc).
 *
 * Visibility states (mutually exclusive):
 *   - hidden       — not in the layout at all (`is-hidden` class)
 *   - entering     — visible but pre-transition (opacity 0, lifted)
 *   - shown        — fully visible (no state classes)
 *   - leaving      — fading out (`is-leaving`), absolutely positioned
 *                    so it doesn't compete for layout with the
 *                    incoming panel
 *
 * The PanelManager drives transitions externally; subclasses only
 * see `onShown` / `onHidden`.
 */

const HIDDEN_CLASS = "is-hidden";
const ENTERING_CLASS = "is-entering";
const LEAVING_CLASS = "is-leaving";

export abstract class BasePanel {
  protected rootElement: HTMLElement | null = null;
  private hostContainer: HTMLElement | null = null;

  /**
   * Public read-only accessor for the root element. Returns null
   * if the panel hasn't been mounted yet. Used by the panel
   * manager to query layout (e.g. sample dark-pixel positions for
   * the transition) without taking a hard dependency on the
   * panel's internal DOM.
   */
  getRootElement(): HTMLElement | null {
    return this.rootElement;
  }

  /**
   * One-time DOM construction. Called by PanelManager the first time
   * this panel is opened. Implementations build their root element
   * (or fragment) and append it to `container`.
   */
  mount(container: HTMLElement): void {
    if (this.rootElement) return;
    this.hostContainer = container;
    this.rootElement = this.buildRootElement();
    this.rootElement.classList.add("studio-panel", HIDDEN_CLASS);
    container.appendChild(this.rootElement);
    this.onMounted();
  }

  /**
   * Mark the panel as entering (visible in DOM but pre-transition).
   * The PanelManager calls this BEFORE removing `is-entering` to
   * trigger the fade-in animation.
   */
  setEntering(): void {
    if (!this.rootElement) return;
    this.rootElement.classList.remove(HIDDEN_CLASS, LEAVING_CLASS);
    this.rootElement.classList.add(ENTERING_CLASS);
  }

  /**
   * Make this panel the visible one. Removes the entering class so
   * the CSS transition runs, then notifies subclasses. Subclasses
   * can override `onShown` to focus inputs, refresh state, etc.
   */
  show(): void {
    if (!this.rootElement) return;
    this.rootElement.classList.remove(HIDDEN_CLASS, ENTERING_CLASS, LEAVING_CLASS);
    this.onShown();
  }

  /**
   * Mark the panel as leaving (still in the DOM, transitioning out).
   * Called by PanelManager during a crossfade; the PanelManager
   * follows up with `hide()` after the transition completes to fully
   * remove the panel from layout.
   */
  setLeaving(): void {
    if (!this.rootElement) return;
    this.rootElement.classList.add(LEAVING_CLASS);
    this.rootElement.classList.remove(ENTERING_CLASS);
  }

  /**
   * Hide without destroying — preserves any in-progress edits.
   */
  hide(): void {
    if (!this.rootElement) return;
    this.rootElement.classList.add(HIDDEN_CLASS);
    this.rootElement.classList.remove(LEAVING_CLASS, ENTERING_CLASS);
    this.onHidden();
  }

  /**
   * Permanent teardown. Subclasses MUST call super to unmount the
   * root element from the host.
   */
  destroy(): void {
    this.onBeforeDestroy();
    if (this.rootElement && this.hostContainer) {
      try {
        this.hostContainer.removeChild(this.rootElement);
      } catch {
        // Already removed — ignore.
      }
    }
    this.rootElement = null;
    this.hostContainer = null;
  }

  // ------------------------------------------------------------------
  // Subclass hooks (no-op defaults)
  // ------------------------------------------------------------------

  /** Build the root element. Required. */
  protected abstract buildRootElement(): HTMLElement;
  /** Called once after the root element is in the DOM. */
  protected onMounted(): void {}
  /** Called every time the panel becomes visible. */
  protected onShown(): void {}
  /** Called every time the panel becomes hidden. */
  protected onHidden(): void {}
  /** Called before the panel is removed from the DOM. */
  protected onBeforeDestroy(): void {}
}
